import { ChevronDownIcon } from '@heroicons/react/24/outline';
import React from 'react';
import videoIcon from '../../../../assets/icons/video.svg';
import { TABLE_FEATURES } from '../caseTypeConstants';
import { TreatmentDetails, TreatmentFeature } from '../treatmentDetails';
import { IntensityDots } from './IntensityDots';

interface CaseTypeTableProps {
    caseTypes: TreatmentDetails[];
    selectedKey?: string;
    onSelect: (caseType: TreatmentDetails) => void;
    onLearnMore: (caseType: TreatmentDetails) => void;
}

const FeatureColumn: React.FC = () => (
    <div className="flex flex-col flex-1 rounded-xl overflow-hidden">
        {/* Header */}
        <div className="h-11 px-4 py-3 bg-gray-100 border-b border-gray-200">
            <span className="font-semibold text-sm text-gray-900">Considerations</span>
        </div>

        {/* Feature rows */}
        {TABLE_FEATURES.map((feature, idx) => {
            const isOdd = idx % 2 === 1;
            return (
                <div
                    key={feature}
                    className={`h-[54px] px-6 py-5 border-b border-gray-200 ${isOdd ? 'bg-gray-100' : 'bg-white'}`}
                >
                    <span className="text-sm text-semibold text-[#6B7280]">{feature}</span>
                </div>
            );
        })}

        {/* Empty learn more row for alignment */}
        <div className="h-[54px] px-6 py-5 invisible">
            <span className="text-sm font-bold text-gray-500">Learn more</span>
        </div>
    </div>
);

const CaseTypeColumn: React.FC<{
    caseType: TreatmentDetails;
    isSelected: boolean;
    onSelect: () => void;
    onLearnMore: () => void;
}> = ({ caseType, isSelected, onSelect, onLearnMore }) => (
    <div
        className={`flex flex-col flex-1 rounded-xl overflow-hidden ${
            isSelected ? 'ring-2 ring-amber-400 z-10' : ''
        }`}
    >
        {/* Header */}
        <div
            className={`h-11 px-4 py-3 border-b border-gray-200 ${
                isSelected ? 'bg-amber-400' : 'bg-gray-100'
            }`}
        >
            <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                    type="radio"
                    name="caseType"
                    checked={isSelected}
                    onChange={onSelect}
                    className="w-4 h-4 accent-gray-900"
                />
                <span className="font-semibold text-sm text-gray-900">{caseType.label}</span>
            </label>
        </div>

        {/* Feature rows */}
        {TABLE_FEATURES.map((feature, idx) => {
            const isOdd = idx % 2 === 1;
            const featureData = caseType.features.find(
                (f: TreatmentFeature) => f.label === feature
            );
            const intensity = featureData?.intensity ?? 0;

            return (
                <div
                    key={feature}
                    className={`h-[54px] px-6 py-5 flex items-center border-b border-gray-200 ${
                        isOdd ? 'bg-gray-100' : 'bg-white'
                    }`}
                >
                    <IntensityDots intensity={intensity} />
                </div>
            );
        })}

        {/* Learn more row */}
        <div
            className={`h-[54px] px-5 py-5 flex items-center gap-2 cursor-pointer hover:bg-gray-50 transition-colors ${
                TABLE_FEATURES.length % 2 === 1 && !isSelected ? 'bg-gray-100' : 'bg-white'
            }`}
            onClick={onLearnMore}
        >
            <img src={videoIcon} alt="" className="w-3.5 h-3.5" />
            <span className="font-bold text-sm text-gray-800">Learn more</span>
        </div>
    </div>
);

export const CaseTypeTable: React.FC<CaseTypeTableProps> = ({
    caseTypes,
    selectedKey,
    onSelect,
    onLearnMore
}) => {
    // Find the index of Traditional Ortho for badge positioning
    const traditionalIndex = caseTypes.findIndex((ct) => ct.monday_key === 'Traditional Ortho');

    return (
        <div className="w-full">
            {/* "Unsure? Pick this one" badge - positioned over Traditional Ortho */}
            {traditionalIndex !== -1 && (
                <div className="flex gap-1 mb-2">
                    {/* Spacer for features column */}
                    <div className="flex-1" />
                    {/* Spacers for columns before Traditional Ortho */}
                    {caseTypes.slice(0, traditionalIndex).map((ct) => (
                        <div key={ct.key} className="flex-1" />
                    ))}
                    {/* Badge centered over Traditional Ortho column */}
                    <div className="flex-1 flex justify-center">
                        <div className="px-4 py-1.5 rounded-full bg-amber-400 text-gray-900 text-sm font-semibold shadow-md flex items-center gap-2">
                            <span>Unsure? Pick this one</span>
                            <ChevronDownIcon className="w-4 h-4" />
                        </div>
                    </div>
                    {/* Spacers for columns after Traditional Ortho */}
                    {caseTypes.slice(traditionalIndex + 1).map((ct) => (
                        <div key={ct.key} className="flex-1" />
                    ))}
                </div>
            )}

            {/* Table with independent columns */}
            <div className="flex gap-1 bg-white rounded-xl">
                <FeatureColumn />
                {caseTypes.map((caseType) => (
                    <CaseTypeColumn
                        key={caseType.key}
                        caseType={caseType}
                        isSelected={selectedKey === caseType.key}
                        onSelect={() => onSelect(caseType)}
                        onLearnMore={() => onLearnMore(caseType)}
                    />
                ))}
            </div>
        </div>
    );
};
